import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  defaultLicenseUsagePageSize,
  useGetCustomerLicenseFacilities,
  useGetCustomerLicenseUsage,
} from 'src/api/customer-license'
import { CenteredSpinner } from 'src/components/Spinner'
import type {
  BillingAsset,
  BillingAssetType,
  BillingCustomer,
  CustomerBillingTab,
} from 'src/generated-types/customer-license'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { BillingHeader } from './components/BillingHeader'
import { CustomerSummarySidebar } from './components/CustomerSummarySidebar'
import { DetailedBreakdownSection } from './components/DetailedBreakdownSection'
import { EditCustomerLicenseRatesModal } from './components/EditCustomerLicenseRatesModal'
import { LocalityActivityLogModal } from './components/LocalityActivityLogModal'
import {
  defaultFirstCustomerId,
  exportBillingPdfReport,
  filterBillingAssets,
  mapFacilitiesToBillingAssets,
  mapLicenseUsageToBillingCustomers,
} from './licenseContainer.action'

export const LicenseContainer = () => {
  const theme = useTheme()
  const { t } = useTranslation('admin-license')
  const pageBg = getThemeColor(theme, MUIThemeColors.backgroundMain)
  const { initialRangeFrom, initialRangeTo } = useMemo(() => {
    const rangeStart = new Date()
    rangeStart.setDate(1)
    const rangeEnd = new Date(rangeStart)
    rangeEnd.setMonth(rangeEnd.getMonth() + 1)
    rangeEnd.setDate(0)
    return { initialRangeFrom: rangeStart, initialRangeTo: rangeEnd }
  }, [])
  const [dateFrom, setDateFrom] = useState<Date | null>(initialRangeFrom)
  const [dateTo, setDateTo] = useState<Date | null>(initialRangeTo)
  const [customerTab, setCustomerTab] = useState<CustomerBillingTab>('active')
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null)
  const [breakdownSearch, setBreakdownSearch] = useState('')
  const [assetType, setAssetType] = useState<BillingAssetType | 'all'>('all')
  const [isExporting, setIsExporting] = useState(false)
  const { showDialog } = useDialogCtx()

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCustomerSearch(customerSearch), 350)
    return () => window.clearTimeout(timer)
  }, [customerSearch])

  const {
    data: usageData,
    isLoading: isUsageLoading,
    isError: isUsageError,
  } = useGetCustomerLicenseUsage({
    page: 0,
    pageSize: defaultLicenseUsagePageSize,
    isActive: customerTab === 'active',
    search: debouncedCustomerSearch.trim() || undefined,
    ...(dateTo && dateFrom ? { from: dateFrom.toISOString(), to: dateTo.toISOString() } : {}),
  })

  useEffect(() => {
    if (isUsageError) {
      toast(t('toastLoadUsageError'), { type: 'error' })
    }
  }, [isUsageError, t])

  const billingCustomers = useMemo(() => mapLicenseUsageToBillingCustomers(usageData?.items ?? []), [usageData?.items])

  useEffect(() => {
    setSelectedCustomerId(prev => {
      if (billingCustomers.length === 0) return null
      if (prev && billingCustomers.some(c => c.id === prev)) return prev
      return defaultFirstCustomerId(billingCustomers)
    })
  }, [billingCustomers])

  const selectedCustomerName = useMemo(
    () => billingCustomers.find(c => c.id === selectedCustomerId)?.name ?? '',
    [billingCustomers, selectedCustomerId],
  )

  const {
    data: facilityRows,
    isLoading: isFacilitiesLoading,
    isError: isFacilitiesError,
  } = useGetCustomerLicenseFacilities(selectedCustomerId, { from: dateFrom, to: dateTo })

  useEffect(() => {
    if (isFacilitiesError) {
      toast(t('toastLoadFacilitiesError'), { type: 'error' })
    }
  }, [isFacilitiesError, t])

  const rawAssets = useMemo(
    () => mapFacilitiesToBillingAssets(facilityRows ?? [], selectedCustomerId ?? '', selectedCustomerName),
    [facilityRows, selectedCustomerId, selectedCustomerName],
  )

  const breakdownRows = useMemo(
    () => filterBillingAssets(rawAssets, selectedCustomerId, breakdownSearch, assetType),
    [rawAssets, selectedCustomerId, breakdownSearch, assetType],
  )

  const onExportPdf = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      toast(t('toastDateRangeRequired'), { type: 'warning' })
      return
    }
    if (billingCustomers.length === 0) {
      toast(t('toastExportNoCustomers'), { type: 'warning' })
      return
    }
    setIsExporting(true)
    try {
      await exportBillingPdfReport({
        from: dateFrom,
        to: dateTo,
        isActive: customerTab === 'active',
        search: debouncedCustomerSearch,
        customerIds: selectedCustomerId != null ? [selectedCustomerId] : billingCustomers.map(c => c.id),
      })
      toast(t('toastExportSuccess'), { type: 'success' })
    } catch {
      toast(t('toastExportError'), { type: 'error' })
    } finally {
      setIsExporting(false)
    }
  }, [dateFrom, dateTo, billingCustomers, customerTab, debouncedCustomerSearch, selectedCustomerId, t])

  const onEditCustomer = useCallback(
    (customer: BillingCustomer) => {
      void showDialog(
        EditCustomerLicenseRatesModal,
        {
          componentProps: {
            customerId: customer.id,
            customerName: customer.name,
            isGlobalServiceStationProvider: customer.isGlobalServiceStationProvider,
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  const onViewLocalityActivityLog = useCallback(
    (asset: BillingAsset) => {
      void showDialog(LocalityActivityLogModal, {
        componentProps: {
          customerId: asset.customerId,
          localityId: asset.entityId,
          localityName: asset.assetName,
        },
        title: (
          <Box>
            <Typography
              variant="h6"
              sx={{ color: getThemeColor(theme, MUIThemeColors.secondaryDark), fontWeight: 700 }}
            >
              {t('activityLogTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {asset.assetName}
            </Typography>
          </Box>
        ),
        opts: { maxWidth: 'sm', fullWidth: true },
      })
    },
    [showDialog, t, theme],
  )

  if (isUsageLoading && !usageData) {
    return (
      <Box sx={{ bgcolor: pageBg, minHeight: '100%', p: 2 }}>
        <CenteredSpinner />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        bgcolor: pageBg,
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box
        className="shadow-md"
        sx={{
          bgcolor: getThemeColor(theme, MUIThemeColors.white),
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 } }}>
          <BillingHeader
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onExportPdf={onExportPdf}
            isExporting={isExporting}
            exportDisabled={billingCustomers.length === 0}
          />
        </Box>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch" sx={{ height: window.innerHeight - 260 }}>
          <CustomerSummarySidebar
            tab={customerTab}
            onTabChange={setCustomerTab}
            search={customerSearch}
            onSearchChange={setCustomerSearch}
            customersList={billingCustomers}
            billingPeriodFrom={dateFrom}
            billingPeriodTo={dateTo}
            selectedCustomerId={selectedCustomerId}
            onSelectCustomer={setSelectedCustomerId}
            onEditCustomer={onEditCustomer}
            onClearSearch={() => setCustomerSearch('')}
          />
          <DetailedBreakdownSection
            search={breakdownSearch}
            onSearchChange={setBreakdownSearch}
            assetType={assetType}
            onAssetTypeChange={setAssetType}
            rows={breakdownRows}
            billingPeriodFrom={dateFrom}
            billingPeriodTo={dateTo}
            facilitiesLoading={!!selectedCustomerId && isFacilitiesLoading}
            onViewLocalityActivityLog={onViewLocalityActivityLog}
          />
        </Stack>
      </Box>
    </Box>
  )
}
